"use client";

import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@vaiu/ui/button";
import { Input } from "@vaiu/ui/input";
import { Label } from "@vaiu/ui/label";
import { Checkbox } from "@vaiu/ui/checkbox";
import QRCodeScanner from './QRCodeScanner';
import RedemptionResult from './RedemptionResult';
import InvoicePaymentDecision from './InvoicePaymentDecision';
import ManualRedemptionUI from './ManualRedemptionUI';
import { useRedeemUx } from './RedeemUxContext';
import type {
  AutoDepositPrefs,
  InvoicePaymentPrefs,
  MerchantProfileForRedeem,
  RedemptionApiResult,
  RevealTokenResponse,
} from './redeemApiTypes';
import { leftoverAfterInvoicePay } from './redeemApiTypes';

interface TokenCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const TokenCodeInput: React.FC<TokenCodeInputProps> = ({ value, onChange, disabled = false }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [values, setValues] = useState<string[]>(Array(6).fill(''));

  // Update local state when prop value changes
  useEffect(() => {
    const newValues = value.split('').concat(Array(6).fill('')).slice(0, 6);
    setValues(newValues);
  }, [value]);

  const updateValue = (newValues: string[]) => {
    setValues(newValues);
    onChange(newValues.join(''));
  };

  const handleChange = (index: number, inputValue: string) => {
    // Only allow letters and numbers, convert to uppercase
    const cleanValue = inputValue.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    if (cleanValue.length <= 1) {
      const newValues = [...values];
      newValues[index] = cleanValue;
      updateValue(newValues);

      // Auto-advance to next input if character entered and not the last input
      if (cleanValue && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      // Move to previous input and clear it
      const newValues = [...values];
      newValues[index - 1] = '';
      updateValue(newValues);
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    if (pasteData.length >= 6) {
      // Fill all 6 boxes with the pasted data
      const newValues = pasteData.split('').slice(0, 6);
      while (newValues.length < 6) newValues.push('');
      updateValue(newValues);
      // Focus the last filled box
      inputRefs.current[Math.min(pasteData.length - 1, 5)]?.focus();
    } else {
      // Fill from current position
      const newValues = [...values];
      for (let i = 0; i < pasteData.length && (index + i) < 6; i++) {
        newValues[index + i] = pasteData[i];
      }
      updateValue(newValues);
      // Focus next empty box or last filled box
      const nextIndex = Math.min(index + pasteData.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleFocus = (index: number) => {
    // Select all text when focusing
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array(6).fill(0).map((_, index) => (
        <Input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          maxLength={1}
          value={values[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className="w-12 h-12 text-center text-lg font-mono font-bold border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
          style={{
            letterSpacing: '0',
          }}
        />
      ))}
    </div>
  );
};

interface RedeemTokenProps {
  initialCode?: string;
  autoSubmit?: boolean;
  onRedemptionComplete?: (result: any) => void;
}

const RedeemToken: React.FC<RedeemTokenProps> = ({ 
  initialCode = '', 
  autoSubmit = false, 
  onRedemptionComplete 
}) => {
  const [code, setCode] = useState(initialCode);
  
  // Debug code changes
  useEffect(() => {
    if (code) {
      console.log('📝 Code state changed to:', code, 'length:', code.length);
    }
  }, [code]);
  const [showScanner, setShowScanner] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redemptionData, setRedemptionData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const [showInvoiceDecision, setShowInvoiceDecision] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [showManualRedemption, setShowManualRedemption] = useState(false);
  const [manualRedemptionData, setManualRedemptionData] = useState<{
    tokenKey: string;
    tokenType: 'cash' | 'invoice';
    amount: number;
    currency: string;
    message?: string;
    conditions?: unknown;
    transactionFee?: number;
    netAmount?: number;
  } | null>(null);
  const [isIDToken, setIsIDToken] = useState(false);
  const {
    platformApi,
    getUser,
    toast,
    i18n,
    emitTokenRedemptionBalanceUpdate,
    emitBalanceUpdate,
  } = useRedeemUx();
  const user = getUser();
  const isSubmittingRef = useRef(false);

  // Debug logging
  useEffect(() => {
    console.log('🚀 RedeemToken mounted with:', { 
      initialCode, 
      autoSubmit, 
      code,
      user: user?._id
    });
    return () => {
      console.log('🔚 RedeemToken unmounted');
    };
  }, []);

  // Auto-submit when initialCode is provided and autoSubmit is true
  useEffect(() => {
    if (initialCode && autoSubmit && !autoSubmitted && initialCode.length === 6) {
      console.log('🔄 Auto-submit effect triggered:', { initialCode, autoSubmit, autoSubmitted });
      setAutoSubmitted(true);
      // Small delay to allow component to render
      setTimeout(() => {
        handleSubmit(null, initialCode);
      }, 500);
    }
  }, [initialCode, autoSubmit, autoSubmitted]);

  const handleScanComplete = (scannedData: string | null) => {
    setShowScanner(false);
    if (!scannedData) {
      setError(i18n.__('redeemToken.noDataScanned'));
      return;
    }

    let extractedCode = scannedData;
    try {
      // Assuming the code is the last part of a path-like string
      const pathParts = scannedData.split('/');
      extractedCode = (pathParts.pop() || '').trim();
    } catch (e) {
      setError(i18n.__('redeemToken.invalidQRCode'));
      return;
    }

    if (extractedCode) {
      const upperCaseCode = extractedCode.toUpperCase();
      setCode(upperCaseCode);
      handleSubmit(null, upperCaseCode);
    } else {
      setError(i18n.__('redeemToken.noCodeExtracted'));
    }
  };

  const handleSubmit = async (e: React.FormEvent | null, codeToSubmit?: string) => {
    if (e) e.preventDefault();
    const finalCode = codeToSubmit || code;

    console.log('Submitting trigger with key:', finalCode);
    
    // Prevent double submission if already loading or submitting
    if (loading || isSubmittingRef.current) {
      console.log('⚠️ Submission already in progress, skipping...', { loading, isSubmitting: isSubmittingRef.current });
      return;
    }
    
    isSubmittingRef.current = true;
    setError(null);
    setRedemptionData(null);
    setLoading(true);

    // Require authentication for token operations
    if (!user) {
      setError(i18n.__('redeemToken.mustBeLoggedIn'));
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    if (!finalCode || finalCode.length !== 6) {
      setError(i18n.__('redeemToken.enterCompleteCode'));
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    try {
      // Extra safety check
      if (!finalCode || finalCode.trim().length !== 6) {
        console.log('⚠️ Invalid code detected before API call:', finalCode);
        setError(i18n.__('redeemToken.invalidTokenCode'));
        setLoading(false);
        isSubmittingRef.current = false;
        return;
      }

      // ID token branch: skip reveal, call trigger with transactor 'identity'
      if (isIDToken) {
        console.log('🪪 ID token redemption, calling trigger with transactor: identity');
        const rawId = await platformApi.redeemToken(finalCode, undefined, 'identity');
        console.log('✅ ID token redemption successful:', rawId);
        const idPayload =
          rawId && typeof rawId === 'object' ? (rawId as Record<string, unknown>) : {};
        setRedemptionData({
          isIDToken: true,
          tokenKey: finalCode,
          ...idPayload,
        });
        setCode('');
        toast({
          title: i18n.__('redeemToken.idTokenRedeemedSuccess'),
          description: i18n.__('redeemToken.tokenRedeemedSuccessDesc', { tokenKey: finalCode }),
        });
        onRedemptionComplete?.(rawId);
        return;
      }

      // Step 1: Always reveal the token first to get token information
      console.log('🔍 Revealing token with key:', finalCode);
      console.log('🔍 User authentication check:', {
        userExists: !!user,
        userId: user?._id,
        userRoles: user?.roles
      });
      const revealResult = (await platformApi.revealToken(
        finalCode
      )) as RevealTokenResponse;
      console.log('✅ Token reveal response:', revealResult);

      // Step 2: Check if this is a multiuse token (auto-redeem)
      if (revealResult.multiuse) {
        console.log('🔄 Multiuse token detected, auto-redeeming...');
        const result = (await platformApi.redeemToken(finalCode)) as RedemptionApiResult;
        console.log('✅ Multiuse token redemption successful:', result);
        
        // Emit balance update event for multiuse token redemption
        if (result?.details?.amount && result?.details?.currency && user?.balances) {
          const currentBalance = user.balances[result.details.currency] || 0;
          emitTokenRedemptionBalanceUpdate(
            result.details.amount,
            result.details.currency,
            currentBalance,
            finalCode
          );
        } else if (result?.amount && result?.currency && user?.balances) {
          const currentBalance = user.balances[result.currency] || 0;
          emitTokenRedemptionBalanceUpdate(
            result.amount,
            result.currency,
            currentBalance,
            finalCode
          );
        }

        setRedemptionData(result);
        setCode('');
        toast({
          title: i18n.__('redeemToken.tokenRedeemedSuccess'),
          description: i18n.__('redeemToken.tokenRedeemedSuccessDesc', { tokenKey: finalCode }),
        });

        if (onRedemptionComplete) {
          onRedemptionComplete(result);
        }
        return;
      }

      // Step 3: Check if this is an invoice token that requires manual payment decision
      if (revealResult.tokenType === 'invoice') {
        console.log('📋 Invoice token detected, checking invoice payment preferences...');
        
        // Check invoice payment preferences
        let invoicePaymentMode = 'always-ask';
        let invoicePaymentThreshold = 0;
        try {
          const invoicePrefs = (await platformApi.getPreferences(
            'invoicePayment'
          )) as InvoicePaymentPrefs;
          invoicePaymentMode = invoicePrefs.mode || 'always-ask';
          invoicePaymentThreshold = invoicePrefs.threshold || 0;
          console.log("📋 Invoice payment preferences:", { mode: invoicePaymentMode, threshold: invoicePaymentThreshold });
        } catch (error) {
          console.warn("Could not load invoice payment preferences:", error);
        }
        
        const invoiceAmount = revealResult.amount || 0;
        const shouldAutoPay = invoicePaymentMode === 'auto-pay' || 
                            (invoicePaymentMode === 'ask-if-above' && invoiceAmount <= invoicePaymentThreshold);
        
        // Get invoice details to check remaining amount (always show redemption panel first)
        const invoiceDetails = (await platformApi.getInvoiceDetails(
          finalCode
        )) as Record<string, unknown>;
        console.log('📋 Invoice details:', invoiceDetails);

        const remaining =
          (invoiceDetails.remainingAmount as number | undefined) ??
          revealResult.amount ??
          0;
        const invAny = invoiceDetails;
        const revAny = revealResult as Record<string, unknown>;
        const invFee = Number(invAny.transactionFee ?? invAny.fee ?? 0) || 0;
        const revFee = Number(revAny.transactionFee ?? revAny.fee ?? 0) || 0;
        const transactionFee = invFee || revFee;
        const totalDebited =
          typeof invAny.totalDebited === 'number'
            ? (invAny.totalDebited as number)
            : remaining + transactionFee;

        if (shouldAutoPay) {
          console.log('💳 Invoice auto-pay: paying full remaining without extra prompts...');
          try {
            const result = (await platformApi.redeemToken(finalCode)) as RedemptionApiResult;
            if (result?.error || result?.status === 'error' || result?.status === 'failed') {
              throw new Error(String(result?.message || result?.error || 'Payment failed'));
            }
            const amount = result.amount;
            const balance = result.balance;
            const details = (result.details || {}) as Record<string, unknown>;
            const currency = (details.currency as string) || result.currency || 'USD';
            if (currency && user?.balances) {
              const currentBalance = user.balances[currency] || 0;
              emitBalanceUpdate({
                currency,
                amountChange: -Math.abs(amount || 0),
                newBalance: balance || 0,
                reason: 'token_redemption',
                tokenKey: details?.paidInvoice as string | undefined,
                metadata: { type: 'invoice_payment' },
              });
            }
            const leftover = leftoverAfterInvoicePay(
              result,
              remaining,
              (details?.amount as number) || amount
            );
            const isPartialPayment = typeof leftover === 'number' && leftover > 0;
            setRedemptionData({
              ...result,
              paidInvoice: details?.paidInvoice,
              amount: (details?.amount as number) || amount,
              currency,
              message: details?.message,
              transactionFee: (details?.transactionFee as number) || 0,
              newBalance: (details?.newBalance as number) || balance,
              remainder: leftover,
              remainingAmount: leftover,
              invoiceRemainder: leftover,
              status: isPartialPayment ? 'partial' : 'completed',
            });
            setCode('');
            toast({
              title: isPartialPayment
                ? i18n.__('redeemToken.partialPaymentSuccess')
                : i18n.__('redeemToken.paymentSuccess'),
              description: i18n.__('redeemToken.paymentSuccessDesc', {
                currency,
                amount: amount || 0,
                isPartial: isPartialPayment ? ` (${leftover ?? 0} remaining)` : '',
              }),
            });
            onRedemptionComplete?.(result);
          } catch (autoPayErr) {
            console.error('❌ Invoice auto-pay failed, showing manual panel:', autoPayErr);
            setInvoiceData({
              invoiceKey: finalCode,
              invoiceAmount: revealResult.amount || 0,
              invoiceCurrency: revealResult.currency || 'USD',
              invoiceMessage: revealResult.message || '',
              invoiceCreator: revealResult.conditions?.redeemer?.name || 'Unknown',
              remainingAmount: remaining,
              transactionFee,
              totalDebited,
              autoPayPreferred: false,
            });
            setShowInvoiceDecision(true);
          }
          return;
        }

        setInvoiceData({
          invoiceKey: finalCode,
          invoiceAmount: revealResult.amount || 0,
          invoiceCurrency: revealResult.currency || 'USD',
          invoiceMessage: revealResult.message || '',
          invoiceCreator: revealResult.conditions?.redeemer?.name || 'Unknown',
          remainingAmount: remaining,
          transactionFee,
          totalDebited,
          autoPayPreferred: false,
        });
        setShowInvoiceDecision(true);
        return;
      }

      // Step 4: Cash tokens — reveal is done; deposit only after panel (auto-deposit shapes defaults) or explicit user action.
      // Note: merchant "auto-accept" is for programmatic / inbox handling, not this redeem UX.
      if (revealResult.tokenType === 'cash') {
        console.log('💰 Cash token detected, checking user preferences...');

        const revAny = revealResult as Record<string, unknown>;
        const tokenAmount = revealResult.amount || 0;
        const transactionFee =
          Number(revAny.transactionFee ?? revAny.fee ?? 0) || 0;
        const netAmount =
          typeof revAny.netAmount === 'number'
            ? (revAny.netAmount as number)
            : Math.max(0, tokenAmount - transactionFee);

        // Check auto-deposit preference for cash tokens
        let shouldAutoDeposit = false;
        if (user) {
          try {
            if (user.roles?.includes("MERCHANT")) {
              // Check merchant preferences for merchant users
              const merchantProfile = (await platformApi.getMerchantProfile()) as MerchantProfileForRedeem;
              // Use new preferences structure, fallback to legacy field
              shouldAutoDeposit = merchantProfile.preferences?.autoDeposit ?? 
                                 merchantProfile.guestCheckoutNotifications?.autoDeposit ?? 
                                 true; // Default to true for merchants
              console.log("🏪 Merchant auto-deposit preference:", shouldAutoDeposit);
            } else {
              // Check personal auto-deposit preferences for non-merchant users
              const personalPreferences = (await platformApi.getPreferences(
                'auto-deposit'
              )) as AutoDepositPrefs;
              shouldAutoDeposit = personalPreferences.autoDepositEnabled || false;
              
              // Check threshold if auto-deposit is enabled
              if (shouldAutoDeposit && personalPreferences.autoDepositThreshold && revealResult.amount) {
                const threshold = personalPreferences.autoDepositThreshold;
                const tokenAmount = revealResult.amount;
                
                // Only auto-deposit if token amount is less than or equal to threshold
                if (tokenAmount > threshold) {
                  shouldAutoDeposit = false;
                  console.log("💰 Token amount exceeds threshold, requiring manual confirmation:", tokenAmount, ">", threshold);
                }
              }
              
              console.log("👤 Personal auto-deposit preference:", shouldAutoDeposit);
            }
          } catch (profileError) {
            console.warn("Could not load auto-deposit preferences:", profileError);
            shouldAutoDeposit = false;
          }
        }

        if (shouldAutoDeposit) {
          console.log('🎛️ Auto-deposit ON: redeeming immediately, then showing result...');
          try {
            const result = (await platformApi.redeemToken(finalCode)) as RedemptionApiResult;
            const redemptionAmount = result?.details?.amount || result?.amount || 0;
            const redemptionCurrency = result?.details?.currency || result?.currency || 'USD';
            if (redemptionAmount && redemptionCurrency && user?.balances) {
              const currentBalance = user.balances[redemptionCurrency] || 0;
              emitTokenRedemptionBalanceUpdate(
                redemptionAmount,
                redemptionCurrency,
                currentBalance,
                finalCode
              );
            }
            setRedemptionData(result);
            setCode('');
            toast({
              title: i18n.__('redeemToken.tokenRedeemedSuccess'),
              description: i18n.__('redeemToken.tokenRedeemedSuccessDesc', { tokenKey: finalCode }),
            });
            onRedemptionComplete?.(result);
          } catch (depositErr) {
            console.error('❌ Auto-deposit failed, showing manual panel:', depositErr);
            setManualRedemptionData({
              tokenKey: finalCode,
              tokenType: revealResult.tokenType,
              amount: tokenAmount,
              currency: revealResult.currency || 'USD',
              message: revealResult.message || '',
              conditions: revealResult.conditions || {},
              transactionFee,
              netAmount,
            });
            setShowManualRedemption(true);
          }
          return;
        }

        console.log('🎛️ Auto-deposit OFF: showing redemption panel before deposit...');
        setManualRedemptionData({
          tokenKey: finalCode,
          tokenType: revealResult.tokenType,
          amount: tokenAmount,
          currency: revealResult.currency || 'USD',
          message: revealResult.message || '',
          conditions: revealResult.conditions || {},
          transactionFee,
          netAmount,
        });
        setShowManualRedemption(true);
        return;
      }

      // Fallback: should not reach here but handle gracefully
      console.warn('⚠️ Unknown token type or flow, falling back to direct redemption');
      const result = (await platformApi.redeemToken(finalCode)) as RedemptionApiResult;
      console.log('✅ Fallback redemption successful:', result);
      
      // Emit balance update event for fallback redemption
              if (result?.details?.amount && result?.details?.currency && user?.balances) {
          const currentBalance = user.balances[result.details.currency] || 0;
          emitTokenRedemptionBalanceUpdate(
            result.details.amount,
            result.details.currency,
            currentBalance,
            finalCode
          );
      } else if (result?.amount && result?.currency && user?.balances) {
          // Fallback to direct properties if details is not available
          const currentBalance = user.balances[result.currency] || 0;
          emitTokenRedemptionBalanceUpdate(
            result.amount,
            result.currency,
            currentBalance,
            finalCode
          );
      }
      
      setRedemptionData(result);
      setCode('');
      toast({
        title: i18n.__('redeemToken.tokenRedeemedSuccess'),
        description: i18n.__('redeemToken.tokenRedeemedSuccessDesc', { tokenKey: finalCode }),
      });

      if (onRedemptionComplete) {
        onRedemptionComplete(result);
      }

    } catch (err: any) {
      console.error('❌ Token processing failed:', err);
      
      // Translate error message if it's an i18n key
      const errorMessage = err.message;
      const displayError = errorMessage?.startsWith('vaiu.') ? i18n.__(errorMessage) : errorMessage;
      
      setError(displayError);
      
      // Show user-friendly error message
      toast({
        title: i18n.__('redeemToken.tokenProcessingFailed'),
        description: displayError || i18n.__('redeemToken.tokenProcessingFailedDesc'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleCloseRedemptionResult = () => {
    setRedemptionData(null);
    
    // Balance updates are now handled by events, no need to refresh profile
    // This prevents the "app refresh issues" mentioned in other components
  };

  const handleInvoicePaymentComplete = (result: any) => {
    console.log('🎉 Invoice payment completed:', result);
    
    // If invoice payment is required, show the payment UI
    if (result.invoicePaymentRequired) {
      console.log('📋 Invoice payment required, showing payment UI');
      const rem = result.remainingAmount ?? result.invoiceAmount;
      setInvoiceData({
        invoiceKey: result.invoiceKey,
        invoiceAmount: result.invoiceAmount,
        invoiceCurrency: result.invoiceCurrency,
        invoiceMessage: result.invoiceMessage || '',
        invoiceCreator: result.invoiceCreator || 'Unknown',
        remainingAmount: rem,
        transactionFee: 0,
        totalDebited: rem,
        autoPayPreferred: false,
      });
      setShowInvoiceDecision(true);
      return;
    }

    // Use the payment result directly from /trigger
    const { amount, balance, details } = result;
    
    // Emit balance update event for invoice payment (balance decreases)
    const currency = details?.currency || result?.currency || 'USD'; // Fix: Add fallback for currency
    if (currency && user?.balances) {
      const currentBalance = user.balances[currency] || 0;
      const balanceChange = -Math.abs(amount || 0); // Negative for payment
      const newBalance = balance || 0; // Use the new balance from response
      
      // Use the general balance update utility since this is a payment (balance decrease)
      emitBalanceUpdate({
        currency: currency,
        amountChange: balanceChange,
        newBalance,
        reason: 'token_redemption',
        tokenKey: details?.paidInvoice,
        metadata: { type: 'invoice_payment' }
      });
    }
    
    console.log('📋 Setting redemption data for invoice payment:', result);
    const leftover = leftoverAfterInvoicePay(
      result,
      invoiceData?.remainingAmount ?? invoiceData?.invoiceAmount,
      (details?.amount as number) || amount
    );
    const isPartialPayment = typeof leftover === 'number' && leftover > 0;
    // Add paidInvoice to the redemption data to indicate this was an invoice payment
    setRedemptionData({
      ...result,
      paidInvoice: details?.paidInvoice, // This triggers the invoice payment UI
      amount: details?.amount || amount,
      currency: currency, // Fix: Use the safe currency variable
      message: details?.message,
      transactionFee: details?.transactionFee || 0,
      newBalance: details?.newBalance || balance,
      remainder: leftover,
      remainingAmount: leftover,
      invoiceRemainder: leftover,
      status: isPartialPayment ? 'partial' : 'completed'
    });
    
    setShowInvoiceDecision(false);
    setInvoiceData(null);
    setCode('');
    
    toast({
      title: isPartialPayment ? i18n.__('redeemToken.partialPaymentSuccess') : i18n.__('redeemToken.paymentSuccess'),
      description: i18n.__('redeemToken.paymentSuccessDesc', { currency: currency, amount: amount || 0, isPartial: isPartialPayment ? ` (${leftover ?? 0} remaining)` : '' }),
    });

    // Call the completion callback if provided
    if (onRedemptionComplete) {
      onRedemptionComplete(result);
    }
  };

  const handleInvoicePaymentError = (error: any) => {
    console.error('❌ Invoice payment error:', error);
    // Translate error message if it's an i18n key
    const errorMessage = error.message || 'Invoice payment failed';
    const displayError = errorMessage?.startsWith('vaiu.') ? i18n.__(errorMessage) : errorMessage;
    setError(displayError);
  };

  const handleInvoiceDecisionCancel = () => {
    console.log('🚫 User canceled invoice payment');
    setShowInvoiceDecision(false);
    setInvoiceData(null);
    setCode('');
    
    toast({
      title: i18n.__('redeemToken.invoicePaymentCanceled'),
      description: i18n.__('redeemToken.invoicePaymentCanceledDesc'),
    });
  };

  const handleManualRedemptionComplete = (result: any) => {
    console.log('✅ Manual redemption completed:', result);
    
    // Store tokenKey before clearing manualRedemptionData
    const tokenKey = manualRedemptionData?.tokenKey || result?.tokenKey;
    
    setShowManualRedemption(false);
    setManualRedemptionData(null);
    
    // Extract amount and currency from result (support multiple response formats)
    const redemptionAmount = result?.details?.amount || result?.amount || 0;
    const redemptionCurrency = result?.details?.currency || result?.currency || 'USD';
    
    // Emit balance update event immediately for UI update
    if (redemptionAmount && redemptionCurrency && user?.balances) {
      const currentBalance = user.balances[redemptionCurrency] || 0;
      console.log('💰 Emitting balance update event for manual redemption:', {
        amount: redemptionAmount,
        currency: redemptionCurrency,
        currentBalance,
        tokenKey
      });
      emitTokenRedemptionBalanceUpdate(
        redemptionAmount,
        redemptionCurrency,
        currentBalance,
        tokenKey
      );
    }
    
    // Show redemption result
    setRedemptionData(result);
    setCode('');
    
    // Call the completion callback if provided
    if (onRedemptionComplete) {
      onRedemptionComplete(result);
    }
  };

  const handleManualRedemptionCancel = () => {
    console.log('🚫 User canceled manual redemption');
    setShowManualRedemption(false);
    setManualRedemptionData(null);
    setCode('');
    
    toast({
      title: i18n.__('redeemToken.tokenRedemptionCanceled'),
      description: i18n.__('redeemToken.tokenRedemptionCanceledDesc'),
    });
  };

  if (showScanner) {
    return <QRCodeScanner onComplete={handleScanComplete} />;
  }

  if (redemptionData) {
    console.log('📊 Showing redemption result:', redemptionData);
    return (
      <RedemptionResult
        redemptionData={redemptionData}
        onClose={handleCloseRedemptionResult}
      />
    );
  }

  if (showInvoiceDecision && invoiceData) {
    return (
      <InvoicePaymentDecision
        invoiceData={invoiceData}
        onPaymentComplete={handleInvoicePaymentComplete}
        onPaymentError={handleInvoicePaymentError}
        onCancel={handleInvoiceDecisionCancel}
      />
    );
  }

  if (showManualRedemption && manualRedemptionData) {
    return (
      <ManualRedemptionUI
        tokenKey={manualRedemptionData.tokenKey}
        tokenType={manualRedemptionData.tokenType}
        amount={manualRedemptionData.amount}
        currency={manualRedemptionData.currency}
        message={manualRedemptionData.message}
        conditions={manualRedemptionData.conditions}
        transactionFee={manualRedemptionData.transactionFee}
        netAmount={manualRedemptionData.netAmount}
        onComplete={handleManualRedemptionComplete}
        onCancel={handleManualRedemptionCancel}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-sm text-muted-foreground">{i18n.__('redeemToken.redeeming')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-3">
          <Label className="text-center block">{i18n.__('redeemToken.tokenCode')}</Label>
          <TokenCodeInput
            value={code}
            onChange={setCode}
            disabled={loading}
          />
          <p className="text-xs text-center text-muted-foreground">
            {i18n.__('redeemToken.enterCode')}
          </p>
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox
              checked={isIDToken}
              onCheckedChange={(checked) => setIsIDToken(!!checked)}
              disabled={loading}
            />
            <span className="text-sm text-muted-foreground">
              {i18n.__('redeemToken.isIDToken')}
              <span className="block text-xs mt-0.5">{i18n.__('redeemToken.idTokenHint')}</span>
            </span>
          </label>
        </div>
        <Button 
          type="submit" 
          className="w-full" 
          disabled={loading || code.length !== 6}
        >
          {i18n.__('redeemToken.redeemToken')}
        </Button>
      </form>
      <Button onClick={() => setShowScanner(true)} className="w-full" variant="secondary">
        {i18n.__('redeemToken.scanQRCode')}
      </Button>
      
      {error && <p className="mt-2 text-sm text-center text-red-600">{error}</p>}
    </div>
  );
};

export default RedeemToken;
